import type { LucideIcon } from "lucide-react";
import {
  LayoutDashboard,
  Users,
  UserCircle,
  Building2,
  CircleDollarSign,
  Briefcase,
  Megaphone,
  BarChart3,
  LayoutGrid,
  LifeBuoy,
  Sparkles,
  PanelsTopLeft,
  Wallet,
  Package,
  Landmark,
  Clock,
  BookOpen,
  Percent,
  PieChart,
  FileText,
  ShieldCheck,
  Settings,
  Receipt,
  FileSpreadsheet,
  Repeat,
  Store,
  ShoppingCart,
  WalletCards,
  TrendingUp,
  Library,
  FolderKanban,
  ListTodo,
  CircleAlert,
  ClipboardList,
  Layers,
} from "lucide-react";
import { cn } from "@/lib/utils";

/** Shared with settings section rail — Zoho-style dark nav rows (both light and dark theme). */
export function cnDashboardModuleNavLink(active: boolean) {
  return cn(
    "relative z-[1] flex w-full cursor-pointer items-center gap-3.5 rounded-md border-l-[3px] py-2 pl-3 pr-2.5 text-left text-[13px] font-medium leading-snug transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/80 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900",
    "[&_svg]:size-4 [&_svg]:shrink-0",
    active
      ? "border-sky-400 bg-slate-800 text-white shadow-sm [&_svg]:text-sky-200"
      : "border-transparent text-slate-300 hover:bg-slate-800/80 hover:text-white [&_svg]:text-slate-500 [&_svg]:hover:text-slate-300",
  );
}

function pickModuleIcon(href: string): LucideIcon {
  const h = href.toLowerCase();
  const d = h.replace(/^\/dashboard/, "");
  const paths = [h, d];

  const match = (frag: string) => paths.some((p) => p.includes(frag) || p === frag || p.endsWith(frag));

  if (match("/crm/overview") || h === "/" || match("/finance") && (h.endsWith("/finance") || d === "/finance"))
    return LayoutDashboard;
  if (match("/leads")) return Users;
  if (match("/contacts")) return UserCircle;
  if (match("/organization") || match("/accounts")) return Building2;
  if (match("/deals")) return CircleDollarSign;
  if (match("/finance/items")) return Package;
  if (match("/finance/banking")) return Landmark;
  if (match("/finance/time")) return Clock;
  if (match("/finance/accounting")) return BookOpen;
  if (match("/finance/taxes")) return Percent;
  if (match("/finance/reports")) return PieChart;
  if (match("/finance/documents")) return FileText;
  if (match("/finance/compliance")) return ShieldCheck;
  if (match("/finance/settings")) return Settings;
  if (match("/finance/sales/accounts") || match("/finance/customers")) return Users;
  if (match("/finance/sales/invoices") || match("/finance/invoices")) return Receipt;
  if (match("/finance/sales/quotes")) return FileSpreadsheet;
  if (match("/finance/sales/recurring")) return Repeat;
  if (match("/finance/sales/retainer")) return WalletCards;
  if (match("/finance/sales/sales-orders")) return Package;
  if (match("/finance/sales/payments-received")) return Landmark;
  if (match("/finance/sales/credit-notes")) return FileText;
  if (match("/finance/sales")) return CircleDollarSign;
  if (match("/finance/purchases/vendors")) return Store;
  if (match("/finance/purchases/bills")) return Receipt;
  if (match("/finance/purchases")) return ShoppingCart;
  if (match("/finance")) return Wallet;
  if (match("/human-resources")) return Briefcase;
  if (match("/marketing")) return Megaphone;
  if (match("/knowledge/articles")) return FileText;
  if (match("/knowledge/books")) return BookOpen;
  if (match("/knowledge/libraries")) return Library;
  if (match("/knowledge")) return Library;
  if (match("/projects/tasks")) return ListTodo;
  if (match("/projects/issues")) return CircleAlert;
  if (match("/projects/list")) return FolderKanban;
  if (match("/projects")) return FolderKanban;
  if (match("/worksuite")) return Layers;
  if (match("/analytics")) return BarChart3;
  if (match("/custom-apps")) return LayoutGrid;
  if (match("/support")) return LifeBuoy;
  if (match("/platform")) return PanelsTopLeft;
  if (match("/settings/profiles")) return ClipboardList;
  if (match("/settings/groups")) return Users;
  if (match("/settings/audit")) return FileText;
  if (match("/settings/users") || match("/users")) return Users;
  if (match("/settings")) return Settings;
  if (match("/crm/sales")) return TrendingUp;
  return PanelsTopLeft;
}

export function DashboardModuleNavIcon({ href }: { href: string }) {
  const Icon = pickModuleIcon(href);
  return <Icon aria-hidden className="size-4 shrink-0" />;
}

export function CustomAppRailIcon() {
  return <Sparkles aria-hidden className="size-4 shrink-0" />;
}
